import { useReactiveVar } from '@apollo/client';
import { get } from 'lodash';
import React, { useState, useEffect } from 'react';
import { selectedPracticeArea } from '../appraisalComparisonCache';
import PracticeCharacterizationChart from './PracticeCharacterizationGraph';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';
import PracticeCharacterizationOrgVsIndGraph from './PracticeCharacterizationOrgVsIndGraph';
import SharedWithFDADisclaimer from '../../shared/components/SharedWithFDADisclaimer';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';

/**
 * @typedef {object} PracticeCharacterizationSectionProps
 * @property {string} [selectedComparisonDataKey]
 * @property {Object} [orgReportData]
 * @property {(data: import('recharts').BarRectangleItem) => void} [practiceClick]
 * @property {Object} [industryViewReportData]
 * @property {boolean} [showPracticeCharacterizationForUnit]
 * @property {boolean} [sharedWithFda]
 * @property {boolean} loading
 * @property {boolean} titleLoading
 */

/**
 * @param {PracticeCharacterizationSectionProps} props
 */
const PracticeCharacterizationSection = props => {
  const {
    industryViewReportData,
    practiceClick,
    loading,
    titleLoading,
    orgReportData,
    selectedComparisonDataKey = FILTER_TYPES_KEYS.Industry,
    showPracticeCharacterizationForUnit = true,
    sharedWithFda = false
  } = props;

  const selectedPracticeAreaId = useReactiveVar(selectedPracticeArea);
  const [practiceArea, setPracticeArea] = useState(null);
  const [graphDescription, setGraphDescription] = useState('');

  useEffect(() => {
    if (selectedComparisonDataKey == FILTER_TYPES_KEYS.OrgVsInd) {
      setGraphDescription(
        'Displays the cumulative industry characterizations against the cumulative ' +
          'organization characterizations for each practice in the selected PA and PGL.'
      );
    } else {
      setGraphDescription(
        'Displays characterizations for each practice in the selected PA and PGL, as it compares to the cumulative' +
          (selectedComparisonDataKey === FILTER_TYPES_KEYS.Industry
            ? ' industry'
            : ' organization') +
          ' characterizations.'
      );
    }
  }, [selectedComparisonDataKey]);

  useEffect(() => {
    const orgPracticeAreas = get(orgReportData, 'organizationAppraisalReport.PracticeAreas', []);
    const industryPracticeAreas = get(
      industryViewReportData,
      'appraisalComparisonView.PracticeAreas',
      []
    );
    const orgSelectedPA =
      orgPracticeAreas.filter(pa => pa.Ids.includes(selectedPracticeAreaId))[0] ||
      orgPracticeAreas[0];
    const indSelectedPA =
      industryPracticeAreas.filter(pa => pa.Ids.includes(selectedPracticeAreaId))[0] ||
      industryPracticeAreas[0];
    let selectedPA =
      selectedComparisonDataKey === FILTER_TYPES_KEYS.Industry ? indSelectedPA : orgSelectedPA;

    if (selectedPA && indSelectedPA && selectedComparisonDataKey === FILTER_TYPES_KEYS.OrgVsInd) {
      const addPractices = selectedPA.Practices.map(v => {
        const practiceInd = indSelectedPA.Practices.filter(pa => pa.Name === v.Name)[0];
        const practiceOrg = orgSelectedPA.Practices.filter(pa => pa.Name === v.Name)[0];
        return {
          ...v,
          SatisfiedInd: practiceInd.Satisfied,
          PartialInd: practiceInd.Partial,
          DeficientInd: practiceInd.Deficient,
          SatisfiedOrg: practiceOrg.Satisfied,
          PartialOrg: practiceOrg.Partial,
          DeficientOrg: practiceOrg.Deficient
        };
      });

      selectedPA = { ...selectedPA, Practices: addPractices };
    }

    // Add Organization's Evaluation information to the Industry data for unit line.
    if (
      selectedComparisonDataKey === FILTER_TYPES_KEYS.Industry &&
      selectedPA &&
      selectedPA.Practices &&
      showPracticeCharacterizationForUnit
    ) {
      const updatedPractices = selectedPA.Practices.map(v => ({
        ...v,
        Evaluation: orgSelectedPA?.Practices.find(pa => pa.Name === v.Name)?.Evaluation
      }));

      selectedPA = { ...selectedPA, Practices: updatedPractices };
    }

    setPracticeArea(selectedPA);
  }, [
    orgReportData,
    selectedPracticeAreaId,
    selectedComparisonDataKey,
    industryViewReportData,
    showPracticeCharacterizationForUnit
  ]);

  return (
    <div id='appraisal-criteria-section'>
      <SkeletonWrapper show={titleLoading}>
        <h4>Practice Characterization</h4>
        <p data-testid='graph-description'>{graphDescription}</p>
      </SkeletonWrapper>
      {!loading && sharedWithFda && <SharedWithFDADisclaimer />}
      <SkeletonWrapper
        show={loading}
        skeletonClassName='section-data-skeleton'
        renderComponent={() =>
          selectedComparisonDataKey === FILTER_TYPES_KEYS.OrgVsInd ? (
            <PracticeCharacterizationOrgVsIndGraph
              practiceArea={practiceArea}
              practiceClick={practiceClick}
            />
          ) : (
            <PracticeCharacterizationChart
              practiceArea={practiceArea}
              practiceClick={practiceClick}
              showPracticeCharacterizationForUnit={showPracticeCharacterizationForUnit}
            />
          )
        }
      />
    </div>
  );
};

export default PracticeCharacterizationSection;
